import * as React from "react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClusterInputState } from "@graylog/server-api";
import usePluginEntities from "hooks/usePluginEntities";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import Routes from "routing/Routes";
import useSetupInputMutations from "components/inputs/InputSetupWizard/hooks/useSetupInputMutations";
import { Button, Row, Col } from "components/bootstrap";
import useInputSetupWizard from "components/inputs/InputSetupWizard/hooks/useInputSetupWizard";
import useInputSetupWizardSteps from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardSteps";
import useInputSetupWizardStepsHelper from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardStepsHelper";
import { INPUT_WIZARD_STEPS, INPUT_WIZARD_FLOWS } from "components/inputs/InputSetupWizard/types";
import ProgressMessage from "components/inputs/InputSetupWizard/steps/components/ProgressMessage";
import { StepWrapper, DescriptionCol, ButtonCol, StyledHeading } from "./components/StepWrapper";
const StartInputStep = () => {
  const inputSetupWizards = usePluginEntities("inputSetupWizard");
  const ExtraSetupWizardStep = useMemo(
    () => {
      var _a;
      return (_a = inputSetupWizards == null ? void 0 : inputSetupWizards.find((plugin) => !!plugin.ExtraSetupWizardStep)) == null ? void 0 : _a.ExtraSetupWizardStep;
    },
    [inputSetupWizards]
  );
  const { checkHasPreviousStep, checkHasNextStep, getStepData } = useInputSetupWizardStepsHelper();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const telemetryPathName = useMemo(() => getPathnameWithoutId(pathname), [pathname]);
  const navigateTo = useNavigate();
  const { goToPreviousStep, orderedSteps, activeStep, wizardData } = useInputSetupWizard();
  const isIlluminateFlow = wizardData.flow === INPUT_WIZARD_FLOWS.ILLUMINATE;
  const { stepsData } = useInputSetupWizardSteps();
  const hasPreviousStep = checkHasPreviousStep(orderedSteps, activeStep);
  const hasNextStep = checkHasNextStep(orderedSteps, activeStep);
  const [startInputStatus, setStartInputStatus] = useState("NOT_STARTED");
  const isRunning = startInputStatus === "RUNNING" || startInputStatus === "ROLLING_BACK";
  const hasBeenStarted = startInputStatus !== "NOT_STARTED";
  const isRollback = startInputStatus === "ROLLING_BACK" || startInputStatus === "ROLLED_BACK";
  const {
    createStreamMutation,
    startStreamMutation,
    createPipelineMutation,
    updateRoutingMutation,
    deleteStreamMutation,
    deletePipelineMutation,
    deleteRoutingRuleMutation,
    connectPipelineMutation
  } = useSetupInputMutations();
  const stepMutations = useMemo(
    () => ({
      createStream: createStreamMutation,
      startStream: startStreamMutation,
      createPipeline: createPipelineMutation,
      setupRouting: updateRoutingMutation,
      connectPipeline: connectPipelineMutation
    }),
    [createStreamMutation, startStreamMutation, createPipelineMutation, updateRoutingMutation, connectPipelineMutation]
  );
  const rollBackMutations = useMemo(
    () => ({
      deleteStream: deleteStreamMutation,
      deletePipeline: deletePipelineMutation,
      deleteRouting: deleteRoutingRuleMutation
    }),
    [deleteStreamMutation, deletePipelineMutation, deleteRoutingRuleMutation]
  );
  const mutationsArray = Object.values(stepMutations);
  const hasError = mutationsArray.some((mutation) => mutation == null ? void 0 : mutation.isError);
  const haveAllSucceeded = mutationsArray.every((mutation) => mutation && !mutation.isPending && mutation.isSuccess);
  let currentStartInputStatus = startInputStatus;
  if (!isRollback && hasError) {
    currentStartInputStatus = "FAILED";
  } else if (!isRollback && haveAllSucceeded) {
    currentStartInputStatus = "SUCCESS";
  }
  const createPipeline = async (stream) => {
    const pipeline = {
      title: stream.title,
      description: `Pipeline for Stream: ${stream.title} created by the Input Setup Wizard.`
    };
    return createPipelineMutation.mutateAsync(pipeline);
  };
  const startInput = async () => {
    const { input } = wizardData;
    if (!input) return;
    ClusterInputState.start(input.id).finally(() => {
      setStartInputStatus("SUCCESS");
    });
  };
  const stopInput = async () => {
    const { input } = wizardData;
    if (!input) return;
    ClusterInputState.stop(input.id);
  };
  const setupInput = async () => {
    var _a;
    if (isIlluminateFlow) {
      startInput();
      return;
    }
    const routingStepData = getStepData(stepsData, INPUT_WIZARD_STEPS.SETUP_ROUTING);
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUT_SETUP_WIZARD.START_INPUT, {
      app_pathname: telemetryPathName,
      app_action_value: "click-input-setup-wizard-start-input",
      chosen_routing_option: (_a = routingStepData == null ? void 0 : routingStepData.streamType) != null ? _a : "UNKNOWN"
    });
    const { input } = wizardData;
    const inputId = input == null ? void 0 : input.id;
    if (!inputId || !routingStepData) return;
    switch (routingStepData.streamType) {
      case "NEW":
        createStreamMutation.mutateAsync(routingStepData.newStream, {
          onSuccess: (streamResponse) => {
            startStreamMutation.mutateAsync(streamResponse.stream_id);
            if (routingStepData.shouldCreateNewPipeline) {
              createPipeline(routingStepData.newStream).then((pipelineResponse) => {
                connectPipelineMutation.mutateAsync({
                  streamId: streamResponse.stream_id,
                  pipelineId: pipelineResponse.id
                });
              });
            }
            updateRoutingMutation.mutateAsync({ input_id: inputId, stream_id: streamResponse.stream_id }).finally(() => {
              startInput();
            });
          }
        });
        break;
      case "EXISTING":
        updateRoutingMutation.mutateAsync({
          input_id: inputId,
          stream_id: routingStepData.streamId,
          remove_from_default: routingStepData.removeMatchesFromDefault
        }).finally(() => {
          startInput();
        });
        break;
      case "DEFAULT":
        startInput();
        break;
      default:
        break;
    }
  };
  const rollback = () => {
    var _a, _b, _c;
    const routingStepData = getStepData(stepsData, INPUT_WIZARD_STEPS.SETUP_ROUTING);
    const createdStreamId = (_a = createStreamMutation.data) == null ? void 0 : _a.stream_id;
    const createdPipelineId = (_b = createPipelineMutation.data) == null ? void 0 : _b.id;
    const routingRuleId = (_c = updateRoutingMutation.data) == null ? void 0 : _c.rule_id;
    switch (routingStepData.streamType) {
      case "NEW":
        stopInput();
        if (routingStepData.shouldCreateNewPipeline && createdPipelineId) {
          deletePipelineMutation.mutateAsync(createdPipelineId);
        }
        if (!createdStreamId) return;
        if (routingRuleId) {
          deleteRoutingRuleMutation.mutateAsync(routingRuleId, {}).finally(() => {
            deleteStreamMutation.mutateAsync(createdStreamId).finally(() => {
              setStartInputStatus("ROLLED_BACK");
            });
          });
        } else {
          deleteStreamMutation.mutateAsync(createdStreamId).finally(() => {
            setStartInputStatus("ROLLED_BACK");
          });
        }
        break;
      case "EXISTING":
        stopInput();
        if (!routingRuleId) return;
        deleteRoutingRuleMutation.mutateAsync(routingRuleId).finally(() => {
          setStartInputStatus("ROLLED_BACK");
        });
        break;
      case "DEFAULT":
        stopInput();
        setStartInputStatus("ROLLED_BACK");
        break;
      default:
        break;
    }
  };
  const handleStart = () => {
    setStartInputStatus("RUNNING");
    setupInput();
  };
  const handleRollback = () => {
    setStartInputStatus("ROLLING_BACK");
    rollback();
  };
  const goToInputDiagnosis = () => {
    const { input } = wizardData;
    if (!input) return;
    navigateTo(Routes.SYSTEM.INPUT_DIAGNOSIS(input.id));
  };
  const handleBackClick = () => {
    goToPreviousStep();
  };
  const isInputStartable = () => {
    if (isIlluminateFlow) {
      return true;
    }
    const routingStepData = getStepData(stepsData, INPUT_WIZARD_STEPS.SETUP_ROUTING);
    if (!routingStepData) return false;
    if (routingStepData.newStream || routingStepData.streamId || routingStepData.streamType === "DEFAULT") return true;
    return false;
  };
  const getProgressEntityName = (stepName, mutations) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const mutation = mutations[stepName];
    const routingStepData = getStepData(stepsData, INPUT_WIZARD_STEPS.SETUP_ROUTING);
    const name = (_d = (_c = (_a = mutation.data) == null ? void 0 : _a.title) != null ? _c : (_b = mutation.data) == null ? void 0 : _b.name) != null ? _d : void 0;
    switch (stepName) {
      case "createStream":
      case "deleteStream":
        return (_e = routingStepData == null ? void 0 : routingStepData.newStream.title) != null ? _e : void 0;
      case "startStream":
        if (routingStepData.streamType === "NEW") {
          return (_f = routingStepData == null ? void 0 : routingStepData.newStream.title) != null ? _f : void 0;
        }
        return name;
      case "createPipeline":
        return (_g = routingStepData == null ? void 0 : routingStepData.newStream.title) != null ? _g : void 0;
      default:
        return name;
    }
  };
  const renderProgressMessages = (mutations) => Object.keys(mutations).map((stepName) => {
    const mutation = mutations[stepName];
    if (!mutation) return null;
    if (mutation.isIdle) return null;
    const name = getProgressEntityName(stepName, mutations);
    return /* @__PURE__ */ React.createElement(
      ProgressMessage,
      {
        stepName,
        isLoading: mutation.isLoading,
        key: stepName,
        isSuccess: mutation.isSuccess,
        name,
        isError: mutation.isError,
        errorMessage: mutation.error
      }
    );
  });
  const renderNextButton = () => {
    if (currentStartInputStatus === "NOT_STARTED" || currentStartInputStatus === "ROLLED_BACK") {
      return /* @__PURE__ */ React.createElement(Button, { onClick: handleStart, disabled: !isInputStartable(), bsStyle: "primary" }, "Start Input");
    }
    if (currentStartInputStatus === "FAILED" || currentStartInputStatus === "ROLLING_BACK") {
      return /* @__PURE__ */ React.createElement(Button, { disabled: currentStartInputStatus === "ROLLING_BACK", onClick: handleRollback, bsStyle: "primary" }, "Rollback Input");
    }
    if (hasNextStep) {
      return /* @__PURE__ */ React.createElement(Button, { disabled: currentStartInputStatus === "RUNNING", onClick: goToInputDiagnosis, bsStyle: "primary" }, "Launch Input Diagnosis");
    }
    return null;
  };
  return /* @__PURE__ */ React.createElement(StepWrapper, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(DescriptionCol, { md: 12 }, /* @__PURE__ */ React.createElement("p", null, "Set up and start the Input according to the configuration made."))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, hasBeenStarted && (isRollback ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledHeading, null, "Rolling back Input..."), renderProgressMessages(rollBackMutations)) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledHeading, null, "Setting up Input..."), renderProgressMessages(stepMutations), currentStartInputStatus && /* @__PURE__ */ React.createElement(
    ProgressMessage,
    {
      stepName: "result",
      isLoading: false,
      isSuccess: currentStartInputStatus === "SUCCESS",
      isError: currentStartInputStatus === "FAILED"
    }
  ), isIlluminateFlow && currentStartInputStatus === "SUCCESS" && /* @__PURE__ */ React.createElement(ExtraSetupWizardStep, null))), !hasBeenStarted && !isInputStartable() && /* @__PURE__ */ React.createElement("p", null, "Your Input is not ready to be setup yet. Please complete the previous steps."))), (hasPreviousStep || hasNextStep) && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(ButtonCol, { md: 12 }, hasPreviousStep && /* @__PURE__ */ React.createElement(Button, { disabled: isRunning, onClick: handleBackClick }, "Back"), renderNextButton())));
};
export default StartInputStep;
